import React, { useCallback, useState } from 'react';

import { IconButton, TextField } from '@mui/material';
import { updatePassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseEye } from '../../../../../../assets/icons/close-eye.svg';
import { ReactComponent as OpenEye } from '../../../../../../assets/icons/open-eye.svg';
import CustomModalNE from '../../../../../../shared/components/ModalNE/CustomModalNE';
import PasswordValidation from '../../../../../../shared/components/PasswordValidation/PasswordValidation';
import StyledButtonNE from '../../../../../../shared/components/StyledButtonNE/StyledButtonNE';
import { TEXT_FIELDS } from '../../../../../../shared/constants/auth';
import {
  CHANGE_PASSWORD,
  CLICK_ACTION,
  SECTION_CONFIGURATION,
  SUB_SECTION_PASS_CANCEL,
  SUB_SECTION_PASS_CONFIRM
} from '../../../../../../shared/constants/tagManager';
import { UPDATE_USER_PASSWORD_SUCCESS_TOAST } from '../../../../../../shared/constants/toast';
import { usePasswordValidation } from '../../../../../../shared/hooks/usePasswordValidation';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_SETTINGS } from '../../../../../../shared/translations/settings';
import { GenericVoidFunction } from '../../../../../../shared/types/genericTypes';
import { getValidationsTexts } from '../../../../../../shared/utils/auth/auth';

import './PasswordChangeModal.scss';

interface PasswordChangeModalProps {
  isModalOpen: boolean;
  handleCloseModal: GenericVoidFunction;
}

const PasswordChangeModal = ({ isModalOpen, handleCloseModal }: PasswordChangeModalProps) => {
  const [userData, setUserData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const validation = usePasswordValidation({
    password: userData.newPassword,
    confirmPassword: userData.confirmPassword
  });

  const canChangePass = useCallback(() => {
    return validation.isValidPassword() && userData.currentPassword;
  }, [validation, userData.currentPassword]);

  const getErrorText = () => {
    return error ? t(TRANSLATIONS_SETTINGS.password.modal.errorPassword) : '';
  };

  const confirmError = () => {
    return !validation.match && validation.confirmPassIsFill;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, currentInput?: boolean) => {
    error && currentInput && setError(false);
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleEyeClick = (input: string) => {
    if (error && input === TEXT_FIELDS.currentInput) {
      return;
    }
    input === TEXT_FIELDS.currentInput && setShowCurrentPass(!showCurrentPass);
    input === TEXT_FIELDS.passwordInput && setShowNewPass(!showNewPass);
    input === TEXT_FIELDS.confirmInput && setShowConfirmPass(!showConfirmPass);
  };

  const getInputProps = (input: string, show: boolean) => {
    return {
      endAdornment: (
        <IconButton onClick={() => handleEyeClick(input)} edge="end" size="large">
          {show ? (
            <CloseEye className="login-icon" data-testid="close-eye-icon" />
          ) : (
            <OpenEye className="login-icon" data-testid="open-eye-icon" />
          )}
        </IconButton>
      )
    };
  };

  const makeRequest = async () => {
    try {
      setLoading(true);
      await updatePassword({ oldPassword: userData.currentPassword, newPassword: userData.newPassword });
      TagManagerService.pushEvent(CLICK_ACTION, CHANGE_PASSWORD, {
        ...SECTION_CONFIGURATION,
        ...SUB_SECTION_PASS_CONFIRM
      });
      handleCloseModal(UPDATE_USER_PASSWORD_SUCCESS_TOAST);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const onModalClose = () => {
    TagManagerService.pushEvent(CLICK_ACTION, CHANGE_PASSWORD, {
      ...SECTION_CONFIGURATION,
      ...SUB_SECTION_PASS_CANCEL
    });
    handleCloseModal();
    setUserData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError(false);
    setShowCurrentPass(false);
    setShowNewPass(false);
  };

  return (
    <CustomModalNE open={isModalOpen} handleClose={onModalClose}>
      <div className="password-change-content-ne">
        <div className="password-change-title">{t(TRANSLATIONS_SETTINGS.password.modal.title)}</div>
        <div className="password-change-form">
          <div className="password-change-fields">
            <TextField
              variant="standard"
              className="input current-password"
              label={t(TRANSLATIONS_SETTINGS.password.modal.currentPassword)}
              id="password"
              name="currentPassword"
              type={showCurrentPass ? 'text' : 'password'}
              value={userData.currentPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, true)}
              error={error}
              helperText={getErrorText()}
              InputProps={getInputProps(TEXT_FIELDS.currentInput, showCurrentPass)}
            />
            <TextField
              variant="standard"
              className="input new-password"
              label={t(TRANSLATIONS_SETTINGS.password.modal.newPassword)}
              id="new-password"
              name="newPassword"
              type={showNewPass ? 'text' : 'password'}
              value={userData.newPassword}
              onChange={handleChange}
              InputProps={getInputProps(TEXT_FIELDS.passwordInput, showNewPass)}
            />
            <TextField
              variant="standard"
              error={confirmError()}
              className="custom-input"
              name="confirmPassword"
              label={t(TRANSLATIONS_SETTINGS.password.modal.confirmPassword)}
              id="reset-password-confirm-password"
              type={showConfirmPass ? 'text' : 'password'}
              margin="dense"
              value={userData.confirmPassword}
              onChange={handleChange}
              helperText={confirmError() ? t(TRANSLATIONS_SETTINGS.password.modal.notMatchPassword) : ''}
              InputProps={getInputProps(TEXT_FIELDS.confirmInput, showConfirmPass)}
            />
          </div>
          <PasswordValidation
            validation={validation}
            length={userData.newPassword.length}
            texts={getValidationsTexts()}
          />
          <div className="password-change-buttons">
            <StyledButtonNE
              buttonType="primary"
              classes="password-change-button"
              disabled={!canChangePass() || loading}
              loading={loading}
              buttonSize="md"
              action={makeRequest}
            >
              {t(TRANSLATIONS_SETTINGS.password.modal.changePassButton)}
            </StyledButtonNE>
            <StyledButtonNE
              buttonType="secondary"
              classes="password-change-button"
              disabled={loading}
              buttonSize="md"
              action={onModalClose}
            >
              {t(TRANSLATIONS_SETTINGS.password.modal.cancelButton)}
            </StyledButtonNE>
          </div>
        </div>
      </div>
    </CustomModalNE>
  );
};

export default PasswordChangeModal;
